import type { SubjectDetailItem } from '@educator/types/subjects'
import type {
  HeroStripColumn,
  HeroStripConfig,
} from '@educator/utils/deriveHeroStrip'
import type { TFunction } from '@mc/i18n'

interface ApplyHeroDetailsArgs {
  details: SubjectDetailItem[]
  heroStrip?: HeroStripConfig
  t: TFunction
}

const resolveColumn = (
  column: HeroStripColumn,
  t: TFunction,
): SubjectDetailItem => ({
  label: t(column.labelKey),
  value: column.value ?? t(column.valueFallbackKey),
})

/**
 * Counterpart to `applyEnrollmentStateDetails` for this page. The detail bar
 * pins the enrollment deadline to the first slot in every state, so
 * the dynamic columns are substituted in place — primary into the `cohort`
 * slot, secondary into `startDate` — with no reordering.
 */
export const applyHeroDetails = ({
  details,
  heroStrip,
  t,
}: ApplyHeroDetailsArgs): SubjectDetailItem[] => {
  const { variant, primaryColumn, secondaryColumn } = heroStrip ?? {}
  if (variant !== 'applications' && variant !== 'nextCohort') return details
  if (!primaryColumn || !secondaryColumn) return details

  const primary = resolveColumn(primaryColumn, t)
  const secondary = resolveColumn(secondaryColumn, t)

  return details.map((item) => {
    if (item.kind === 'cohort') return primary
    if (item.kind === 'startDate') return secondary
    return item
  })
}
